import { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useDispatchApp } from 'redux/rootSelectors';
import { parsingDate } from 'utils/momentParse';
import moment from 'moment';
import { TTableSort } from 'types/general';
import i18n from 'i18n';
import { checkedLocalization } from 'utils/checkedLocalization';
import { useUserInfo } from 'domains/user/model/selectors';
import {
  Column,
  IOnSubmitParams,
  IReportBuilderFormik,
  TGetReportData,
  TGroupingItem,
  TGroupingsState,
  TObjectionTypes,
  TSelectOptionsType,
  TUseReportBuilderData,
} from '../types';
import {
  useNextStepData,
  useNotExistInCatalogKeys,
  useReportByFilter,
  useReportSettings,
  useReportsList,
} from '../model/selectors';
import { useReportBuilderEffects } from './useReportBuilderEffects';
import { getCurrentObjection, setFiltersToParams } from '../utils';
import { getReport } from '../model/actions';
import { setClearNextStep, setClearReport } from '../reducer';

const DATE_FORMAT = 'DD-MM-YYYY';

export const useReportBuilder = (): TUseReportBuilderData => {
  const dispatch = useDispatchApp();

  const { data: reportsTypeList, isLoading: isReportsTypeLoading } =
    useReportsList();
  const { data: settings, isLoading: isReportsSettingsLoading } =
    useReportSettings();
  const { data: reports, isLoading: isReportsLoading } = useReportByFilter();
  const { isLoading: isNextStepLoading } = useNextStepData();
  const { isLoading: isNotExistKeysLoading } = useNotExistInCatalogKeys();
  const { data: user } = useUserInfo();

  const [reportTypeId, setReportTypeId] = useState<number | null>(null);
  const [tableSort, setTableSort] = useState<TTableSort | null>(null);
  // Состояние для grouping
  const [groupings, setGroupings] = useState<TGroupingsState>({});

  // Состояние для filtering
  const [filterings, setFilterings] = useState<TGroupingsState>({});

  // Состояние для objection
  const [objections, setObjections] = useState<
    Record<TObjectionTypes, TGroupingItem>
  >({} as Record<TObjectionTypes, TGroupingItem>);

  // Состояние для statistic
  const [statistics, setStatistics] = useState<TSelectOptionsType[]>([]);

  // Данные для колонок
  const [columns, setColumns] = useState<Column[]>([]);

  const formik = useFormik<IReportBuilderFormik>({
    initialValues: {
      reportType: null,
      period: {
        from: parsingDate(moment()).format('YYYY-MM-DD'),
        to: parsingDate(moment().add(7, 'days')).format('YYYY-MM-DD'),
      },
      reportFilters: {
        groupBy: {},
        filterBy: {},
        objectionBy: {},
        statisticBy: {},
      },
    },
    onSubmit: (values) => {
      let params: IOnSubmitParams = {
        filtering: {},
        grouping: {},
        objection: {},
        statisticField: [],
      };

      const { groupBy, filterBy, objectionBy, statisticBy } =
        values.reportFilters;

      params = setFiltersToParams(
        params,
        settings?.filtering,
        filterBy,
        'filtering',
      );

      params = setFiltersToParams(
        params,
        settings?.grouping,
        groupBy,
        'grouping',
      );

      params = getCurrentObjection(params, objectionBy, objections);

      params.statisticField = Object.keys(statisticBy).filter(
        (key) => statisticBy[key],
      );

      Object.entries(params.filtering).map(([key, value]) => {
        if (Array.isArray(value) && !value[0]) {
          delete params.filtering[key];
        }
        return key;
      });

      Object.entries(params.grouping).map(([key, value]) => {
        if (Array.isArray(value) && !value[0]) {
          delete params.grouping[key];
        }
        return key;
      });

      dispatch(
        getReport({
          id: reportTypeId as number,
          period: values.period,
          data: params,
        }),
      );
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const resetForm = () => {
    dispatch(setClearNextStep());
    dispatch(setClearReport());
    
    if (objections && Object.entries(objections).length > 0) {
      const startObjectionsElement = Object.fromEntries([
        Object.entries(objections).filter((obj) => Boolean(obj[1]))[0],
      ]) as React.SetStateAction<Record<TObjectionTypes, TGroupingItem>>;

      setObjections(startObjectionsElement);
    }

    const dateDefaults: Record<string, any> = {};
    Object.entries(groupings).forEach(([key, value]) => {
      if (
        value.sql_key === 'date' &&
        Array.isArray(value.data) &&
        value.data.length > 1
      ) {
        // eslint-disable-next-line prefer-destructuring
        dateDefaults[key] = value.data[1];
      }
    });

    const initialReportFilters = formik.initialValues.reportFilters || {};

    const resetValues = {
      ...formik.initialValues,
      period: formik.values.period,
      reportType: formik.values.reportType,
      reportFilters: {
        ...initialReportFilters,
        groupBy: {
          ...(initialReportFilters.groupBy || {}),
          ...dateDefaults,
        },
      },
    } as any;
    formik.resetForm({ values: resetValues });
  };

  const isGlobalLoading =
    isNextStepLoading ||
    isNotExistKeysLoading ||
    isReportsTypeLoading ||
    isReportsSettingsLoading;

  const reportsListToSelectOptions = useMemo(() => {
    if (reportsTypeList) {
      return reportsTypeList.map((report, index) => ({
        label: checkedLocalization(
          `reportbuilder.types.${reportsTypeList[index].title}`,
          reportsTypeList[index].title,
          false,
        ),
        value: report.title,
        id: String(report.id),
      }));
    }
    return [
      {
        label: '',
        value: '',
      },
    ];
  }, [reportsTypeList, i18n.language]);

  const sortedReports = useMemo(() => {
    if (!reports) {
      return [];
    }

    const formatReportDate = (value: string) => {
      if (!value) {
        return value;
      }

      const hasTime = /\d{2}:\d{2}/.test(value);

      if (hasTime) {
        const dateWithTime = moment(
          value,
          ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601],
          true,
        );

        if (dateWithTime.isValid()) {
          return dateWithTime.format('DD-MM-YYYY HH:mm:ss');
        }
      }

      return moment(value).format(DATE_FORMAT);
    };

    const preparedReports: TGetReportData = reports.map((report) => ({
      ...report,
      date: formatReportDate(report.date),
    }));

    if (!tableSort?.key || !tableSort.sort) {
      return preparedReports;
    }

    const { key, sort } = tableSort;

    return preparedReports.sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      if (aValue === bValue) {
        return 0;
      }

      if (aValue == null) {
        return 1;
      }

      if (bValue == null) {
        return -1;
      }

      let result: number;

      if (key === 'date') {
        const parseDateValue = (dateValue: string | number) => {
          const normalizedValue = String(dateValue);
          const dateFormats = /\d{2}:\d{2}/.test(normalizedValue)
            ? ['DD-MM-YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601]
            : [DATE_FORMAT, 'YYYY-MM-DD', moment.ISO_8601];

          return moment(normalizedValue, dateFormats, true);
        };

        result = parseDateValue(aValue).diff(parseDateValue(bValue));
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        result = aValue - bValue;
      } else {
        result = String(aValue).localeCompare(String(bValue), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }

      return sort === 'ASC' ? result : -result;
    });
  }, [reports, tableSort]);

  const currentUserStep: TObjectionTypes = useMemo(() => {
    switch (user?.partner.type) {
      case 'ORGANIZATION':
        return 'agency';
      case 'AGENCY':
        return 'client';
      case 'CLIENT':
        return 'campaign';

      default:
        return 'agency';
    }
  }, [user?.partner.type]);

  const handleSort = ({ key, sort }: TTableSort) => {
    setTableSort({
      key,
      sort,
    });
  };

  useReportBuilderEffects({
    reportTypeId,
    reportsTypeList,
    formik,
    groupings,
    reports,
    setReportTypeId,
    setGroupings,
    setFilterings,
    setObjections,
    setStatistics,
    setColumns,
    resetForm,
  });

  return {
    formik,
    reportsTypeList,
    reports: sortedReports,
    columns,
    isReportsLoading,
    isReportsTypeLoading,
    reportsListToSelectOptions,
    groupings,
    filterings,
    objections,
    statistics,
    currentUserStep,
    isGlobalLoading,
    setReportTypeId,
    setObjections,
    handleSort,
    tableSort,
    resetForm,
  };
};
